import { useTranslation } from 'react-i18next'

import { useCSVDownloader, useFoundations, usePermissions } from '@percent/admin-dashboard/common/hooks'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { Button, Menu } from '@percent/lemonade'
import { ExportProps } from './Export.types'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

export function Export({
  id,
  endDate,
  foundationId,
  disbursementBatchId,
  detailedCounts,
  summary
}: Readonly<ExportProps>) {
  const { t } = useTranslation()
  const { foundations } = useFoundations()
  const { userHasPermissions } = usePermissions()
  const internalFgaDoraDisbursementsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DISBURSEMENTS
  )
  const {
    reportingService: { getDisbursementBatchTrusteeReport, getDisbursementBatchPaymentReport }
  } = useServices()

  const canDownloadReport =
    !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_download_report')

  const canDownloadBatchPaymentFile =
    !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_download_batch_file')

  const foundationName = foundations?.[foundationId]?.name
  const foundationCountryCode = foundations?.[foundationId]?.countryCode

  const [{ loading: reportLoading, errorCSVMessage: reportErrorMessage }, { downloadCSV: downloadReportCSV }] =
    useCSVDownloader({
      service: getDisbursementBatchTrusteeReport(id),
      fileName: `${foundationCountryCode}_${foundationName}_disbursement_end_${dayJS(endDate).format('YYYY-MM-DD')}`,
      successMessage: t('toast.trusteeReportSuccess')
    })

  const handleReportExport = () => {
    if (downloadReportCSV) {
      downloadReportCSV()
    }
  }

  const handleBatchPaymentFileExport = () => {
    if (downloadBatchPaymentFileCSV) {
      downloadBatchPaymentFileCSV()
    }
  }

  const [
    { loading: batchPaymentFileLoading, errorCSVMessage: batchPaymentFileErrorMessage },
    { downloadCSV: downloadBatchPaymentFileCSV }
  ] = useCSVDownloader({
    service: getDisbursementBatchPaymentReport(disbursementBatchId),
    fileName: `${foundations ? foundations[foundationId]?.countryCode : foundationId}_payments_to_${dayJS(
      endDate
    ).format('YYYY-MM-DD')}_on_${dayJS().format('YYYY-MM-DD_HH-mm-ss-SSS')}`,
    successMessage: t('toast.downloadBatchPaymentFileSuccess'),
    errorMessage: t('toast.downloadBatchPaymentFileError')
  })

  if (reportErrorMessage) {
    return <ErrorView errorMessage={reportErrorMessage} />
  }

  if (batchPaymentFileErrorMessage) {
    return <ErrorView errorMessage={batchPaymentFileErrorMessage} />
  }

  const approvedBankTransfersCount = detailedCounts.find(
    ({ type, status: detailedCountStatus }) =>
      type === 'bank_transfer' && (detailedCountStatus === 'approved' || detailedCountStatus === 'approvalRequired')
  )?.count

  const shouldShowBatchPaymentFile = summary.some(({ status }) => status === 'approved') && approvedBankTransfersCount

  return (
    <Menu
      customTriggerComponent={
        <Button size="small" variant="secondary">
          {t('button.export')}
        </Button>
      }
      sections={[
        {
          items: [
            {
              key: 'report',
              label: t('button.report'),
              onClick: handleReportExport,
              shouldBeDisabled: reportLoading || !canDownloadReport,
              isLoading: reportLoading
            },
            ...(shouldShowBatchPaymentFile
              ? [
                  {
                    key: 'disbursementBatchReport',
                    label: t('button.downloadBatchPaymentFile'),
                    onClick: handleBatchPaymentFileExport,
                    shouldBeDisabled: batchPaymentFileLoading || !canDownloadBatchPaymentFile,
                    isLoading: batchPaymentFileLoading
                  }
                ]
              : [])
          ]
        }
      ]}
    />
  )
}
